/**
 * Browser runtime public configuration (window.__EFM_CONFIG__), generated at
 * container start from ECS env. Under Vite dev/test, falls back to import.meta.env.VITE_*.
 */

export interface EfmPublicRuntimeConfig {
	apiBaseUrl: string;
	institutionUniversalId: string;
	awsBucketName: string;
	smbBucketName: string;
	efmIdentifier: string;
	launchDarklyClientId: string;
	launchDarklyEnabled: boolean;
}

declare global {
	interface Window {
		__EFM_CONFIG__?: Partial<EfmPublicRuntimeConfig> & Record<string, unknown>;
	}
}

function readWindowPartial(): Partial<EfmPublicRuntimeConfig> | undefined {
	if (typeof window === "undefined") return undefined;
	const cfg = window.__EFM_CONFIG__;
	if (!cfg || typeof cfg !== "object") return undefined;
	return cfg as Partial<EfmPublicRuntimeConfig>;
}

/** True when window config looks populated (production entrypoint), not an empty dev stub. */
function windowConfigLooksPopulated(w: Partial<EfmPublicRuntimeConfig>): boolean {
	return !!(w.institutionUniversalId && w.efmIdentifier);
}

function fromViteEnv(): EfmPublicRuntimeConfig {
	const env = import.meta.env;
	const ldEnabled = env.VITE_LAUNCHDARKLY_ENABLED !== "false";
	return {
		apiBaseUrl: String(env.VITE_API_BASE_URL ?? ""),
		institutionUniversalId: String(env.VITE_INSTITUTION_UNIVERSAL_ID ?? ""),
		awsBucketName: String(env.VITE_AWS_BUCKET_NAME ?? ""),
		smbBucketName: String(env.VITE_SMB_BUCKET_NAME ?? ""),
		efmIdentifier: String(env.VITE_EFM_IDENTIFIER ?? ""),
		launchDarklyClientId: String(env.VITE_LAUNCHDARKLY_CLIENT_ID ?? ""),
		launchDarklyEnabled: ldEnabled,
	};
}

function mergePreferWindow(
	win: Partial<EfmPublicRuntimeConfig> | undefined,
	fallback: EfmPublicRuntimeConfig
): EfmPublicRuntimeConfig {
	const ldWin = win?.launchDarklyEnabled;
	let launchDarklyEnabled = fallback.launchDarklyEnabled;
	if (typeof ldWin === "boolean") {
		launchDarklyEnabled = ldWin;
	} else if (typeof ldWin === "string") {
		launchDarklyEnabled = ldWin !== "false";
	} else if (ldWin !== undefined) {
		launchDarklyEnabled = Boolean(ldWin);
	}

	return {
		apiBaseUrl: win?.apiBaseUrl ?? fallback.apiBaseUrl,
		institutionUniversalId: win?.institutionUniversalId ?? fallback.institutionUniversalId,
		awsBucketName: win?.awsBucketName ?? fallback.awsBucketName,
		smbBucketName: win?.smbBucketName ?? fallback.smbBucketName,
		efmIdentifier: win?.efmIdentifier ?? fallback.efmIdentifier,
		launchDarklyClientId: win?.launchDarklyClientId ?? fallback.launchDarklyClientId,
		launchDarklyEnabled,
	};
}

export function getRuntimeConfig(): EfmPublicRuntimeConfig {
	const win = readWindowPartial();
	const viteFallback = fromViteEnv();
	const useViteMerge = import.meta.env.DEV || import.meta.env.MODE === "test";

	if (win && windowConfigLooksPopulated(win)) {
		return mergePreferWindow(win, viteFallback);
	}
	if (useViteMerge) {
		return mergePreferWindow(win, viteFallback);
	}
	return mergePreferWindow(win, {
		apiBaseUrl: "",
		institutionUniversalId: "",
		awsBucketName: "",
		smbBucketName: "",
		efmIdentifier: "",
		launchDarklyClientId: "",
		launchDarklyEnabled: true,
	});
}
